function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import find from 'lodash/find';
import get from 'lodash/get';
import partition from 'lodash/partition';
import { all, call, put, takeLatest, getContext, select } from 'redux-saga/effects';
import ApiService from 'services/ApiService';
import CountryLocalizationService from 'services/CountryLocalizationService';
import App from 'modules/App';
import OidcService from 'services/OidcService';
import * as actionTypes from './actionTypes';
import * as actions from './actions';
import { isMinor } from './helpers';
function* getInformation() {
    const requestUrl = '/api/Information/Account';
    return yield call(ApiService.regionalRequest, requestUrl);
}
function* getConsentsTemplate(countryCode, languageCode, scopeName) {
    const requestUrl = '/api/Localization/legalConsents' + `?countryCode=${countryCode}&languageCode=${languageCode}&scopes=${scopeName}`;
    return yield call(ApiService.originalRequest, requestUrl);
}
function* getGlobalConsentsTemplate(countryCode, languageCode, scopeName) {
    const apiUrl = yield getContext('apiUrl');
    const region = yield select(App.selectors.originalRegionName);
    const requestUrl = `${apiUrl}/aid/api/${region}/Localization/legalConsents` + `?countryCode=${countryCode}&languageCode=${languageCode}&scopes=${scopeName}`;
    return yield call(ApiService.plainRequest, requestUrl);
}
function* checkInformation() {
    try {
        const tokenData = yield call(OidcService.getTokenData);
        const scopeName = get(tokenData, 'identity_scope');
        const countryCode = get(tokenData, 'identity_country_code');
        const languageCode = yield select(App.selectors.langCode);
        const [information, informationTemplate, consentsTemplate, globalConsentsTemplate] = yield all([
            call(getInformation),
            call(CountryLocalizationService.fetchInformationTemplate, countryCode, scopeName),
            call(getConsentsTemplate, countryCode, languageCode, scopeName),
            call(getGlobalConsentsTemplate, countryCode, languageCode, scopeName)
        ]);
        const { dateOfBirth } = information;
        const isUserMinor = dateOfBirth && isMinor(dateOfBirth);
        const [mandatoryConsents, optionalConsents] = partition(globalConsentsTemplate, {
            isMandatory: true
        });
        // const [mandatoryConsents] = partition(globalConsentsTemplate, { isMandatory: true });
        let isNeedToFillUp = false;
        for(let i = 0; i < mandatoryConsents.length; i++){
            const { legalConsentId } = mandatoryConsents[i];
            const approval = find(information.legalConsentApprovals, {
                legalConsentId
            });
            if (!approval || !approval.decision) {
                isNeedToFillUp = true;
                break;
            }
        }
        if (!isUserMinor) {
            const guardedOptionalConsents = optionalConsents.filter((consent)=>[
                    'SensitiveDataAndProfiling',
                    'Email'
                ].includes(consent.type));
            if (!isNeedToFillUp) {
                for(let i = 0; i < guardedOptionalConsents.length; i++){
                    const { legalConsentId } = guardedOptionalConsents[i];
                    const approval = find(information.legalConsentApprovals, {
                        legalConsentId
                    });
                    if (!approval) {
                        isNeedToFillUp = true;
                        break;
                    }
                }
            }
        }
        if (!isNeedToFillUp) {
            for(let i = 0; i < informationTemplate.length; i++){
                const ft = informationTemplate[i];
                const value = get(information, ft.name);
                if (!value && ft.isMandatory) {
                    isNeedToFillUp = true;
                    break;
                }
            }
        }
        if (isNeedToFillUp) {
            const isMobileDisplayMode = yield select(App.selectors.isMobileDisplayMode);
            yield put(App.actions.goToAid(isMobileDisplayMode ? 'mobile/set-information' : 'set-information'));
            return;
        }
        yield put(actions.checkInformationSuccess(information, informationTemplate, consentsTemplate, globalConsentsTemplate));
    } catch (err) {
        yield put(actions.checkInformationError(err));
        yield call(App.dispatchError, err);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* fetchConsentsTemplate() {
    try {
        const tokenData = yield call(OidcService.getTokenData);
        const scopeName = get(tokenData, 'identity_scope');
        const countryCode = get(tokenData, 'identity_country_code');
        const languageCode = yield select(App.selectors.langCode);
        const [consentsTemplate, globalConsentsTemplate] = yield all([
            call(getConsentsTemplate, countryCode, languageCode, scopeName),
            call(getGlobalConsentsTemplate, countryCode, languageCode, scopeName)
        ]);
        yield put(actions.fetchConsentsTemplateSuccess(consentsTemplate, globalConsentsTemplate));
    } catch (err) {
        yield put(actions.fetchConsentsTemplateError(err));
        yield call(App.dispatchError, err);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* updateInformation({ payload }) {
    try {
        const { informationUpdate } = payload;
        const requestUrl = '/api/Information/Account';
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'PUT',
            body: _object_spread({}, informationUpdate)
        });
        yield put(actions.updateInformationSuccess(informationUpdate));
    } catch (err) {
        yield put(actions.updateInformationError(err));
        yield call(App.dispatchError, err);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* getOrganizationMembership() {
    const requestUrl = '/api/Information/OrganizationMembership';
    return yield call(ApiService.regionalRequest, requestUrl);
}
function* checkOrganizationMembership() {
    try {
        const organizationMemberships = yield call(getOrganizationMembership);
        if (organizationMemberships.length === 0) {
            yield put(App.actions.goToAid('organization-memberships'));
            return;
        }
        yield put(actions.checkOrganizationMembershipsSuccess(organizationMemberships));
    } catch (err) {
        yield put(actions.checkOrganizationMembershipsError(err));
        yield call(App.dispatchError, err);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* sagas() {
    yield takeLatest(actionTypes.CHECK_INFORMATION, checkInformation);
    yield takeLatest(actionTypes.FETCH_CONSENTS_TEMPLATE, fetchConsentsTemplate);
    yield takeLatest(actionTypes.UPDATE_INFORMATION, updateInformation);
    yield takeLatest(actionTypes.CHECK_ORGANIZATION_MEMBERSHIPS, checkOrganizationMembership);
}
export default [
    sagas
];
